import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, Send, ArrowLeft, MapPin, Clock, Users, Loader2, Check, Quote } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StreetStoriesPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [timeline, setTimeline] = useState("");
  const [relType, setRelType] = useState("");
  const [story, setStory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/street-stories`).then(r => setStories(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!story.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/street-stories/submit`, {
        name: name.trim() || "Anonymous",
        location: location.trim() || null,
        story: story.trim(),
        recovery_timeline: timeline.trim() || null,
        relationship_type: relType || null,
      });
      setSubmitted(true);
      toast.success("Your story has been submitted for review.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="street-stories-section">
        <div className="container mx-auto max-w-5xl">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-8 text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          <div className="text-center mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2A3A] mb-3" data-testid="street-stories-title">
              Stories from the Streets
            </h1>
            <p className="text-sm text-[#1A2A3A]/60 max-w-lg mx-auto">
              Real recovery profiles from real people. Every story is a testament to the human spirit. Yours could be the one that helps someone take their first step.
            </p>
          </div>

          {/* Stories Grid */}
          <div className="space-y-5 mb-12">
            {loading ? (
              [1,2,3].map(i => <div key={i} className="animate-pulse h-36 bg-white rounded-xl" />)
            ) : (
              stories.map((s, i) => (
                <Card key={i} className="bg-white rounded-2xl shadow-card border border-[#E5E7EB]/50 overflow-hidden" data-testid={`street-story-${i}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4FB3D4]/20 to-[#A69BE0]/20 flex items-center justify-center flex-shrink-0">
                        <Heart className="h-5 w-5 text-[#FFB7B2]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className="font-semibold text-[#1A2A3A] text-sm">{s.name || "Anonymous"}</span>
                          {s.location && (
                            <span className="flex items-center gap-1 text-[10px] text-[#1A2A3A]/50"><MapPin className="h-3 w-3" /> {s.location}</span>
                          )}
                          {s.recovery_timeline && (
                            <span className="flex items-center gap-1 text-[10px] text-[#4FB3D4]"><Clock className="h-3 w-3" /> {s.recovery_timeline}</span>
                          )}
                          {s.relationship_type && (
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#A69BE0]/10 text-[#A69BE0]">{s.relationship_type}</span>
                          )}
                        </div>
                        <p className="text-sm text-[#1A2A3A]/70 leading-relaxed italic">"{s.story}"</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Submission Form */}
          <Card className="rounded-2xl shadow-floating border-0" data-testid="street-story-form">
            <CardContent className="p-8">
              {submitted ? (
                <div className="text-center py-8">
                  <Check className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-2">Thank You for Your Courage</h3>
                  <p className="text-sm text-[#1A2A3A]/60 mb-4">Your story has been submitted and will be reviewed. You are helping build a community of unstoppable survivors.</p>
                  <Button onClick={() => setSubmitted(false)} variant="outline" className="rounded-full px-6 py-3" data-testid="submit-another-street-btn">Share Another Story</Button>
                </div>
              ) : (
                <>
                  <h2 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-1">Share Your Recovery Profile</h2>
                  <p className="text-xs text-[#1A2A3A]/50 mb-5">All submissions reviewed before publishing. You can remain anonymous.</p>
                  <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Name (optional)</label>
                      <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Anonymous" className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="ss-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Location (optional)</label>
                      <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="City, State" className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="ss-location" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Recovery Timeline</label>
                      <input type="text" value={timeline} onChange={e => setTimeline(e.target.value)} placeholder="e.g., 2 years in recovery" className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="ss-timeline" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Relationship Type</label>
                      <select value={relType} onChange={e => setRelType(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="ss-type">
                        <option value="">Select</option>
                        <option value="romantic">Romantic Partner</option>
                        <option value="family">Family Member</option>
                        <option value="workplace">Workplace</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Your Story *</label>
                      <textarea value={story} onChange={e => setStory(e.target.value)} rows={5} required placeholder="Tell us your journey — where you were, what happened, and where you are now. Your words could save someone's life." className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm resize-none" data-testid="ss-story" />
                    </div>
                    <div className="md:col-span-2">
                      <Button type="submit" disabled={!story.trim() || submitting} className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-8 py-4 font-semibold text-sm" data-testid="ss-submit-btn">
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                        {submitting ? "Submitting..." : "Submit Your Story"}
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <footer className="py-8 px-6 bg-[#1A2A3A] text-white text-center">
        <p className="text-white/40 text-xs">&copy; 2025 ThryvLife by Brian Bass (BriVonB). All rights reserved.</p>
      </footer>
    </div>
  );
};

export default StreetStoriesPage;
